import React from 'react';

// Global App Styles - Mobile-First Fixed Dimensions
export const APP_STYLES = {
  // Fixed dimensions - consistent across all devices
  navHeight: 70,
  headerHeight: 72,
  buttonHeight: 48,
  inputHeight: 52,
  inputHeightSm: 40,
  cardRadius: 12,

  // Spacing - Mobile optimized with proper breathing room
  spacingXs: 4,
  spacingSm: 8,
  spacingMd: 12,
  spacingLg: 16,
  spacingXl: 24,

  // Typography - Readable on mobile
  fontSizeXs: 10,
  fontSizeSm: 12,
  fontSizeBase: 14,
  fontSizeMd: 15,
  fontSizeLg: 16,
  fontSizeXl: 20,
  fontSizeXxl: 24,

  // Max content width for app-like feel
  maxContentWidth: 480,
};

// Heisenberg Theme - Breaking Bad inspired
const THEME = {
  name: 'Heisenberg',
  bgPrimary: '#0a0a0a',
  bgSecondary: '#111111',
  bgTertiary: '#1a1a1a',
  bgCard: 'rgba(20, 20, 20, 0.98)',
  bgInput: 'rgba(30, 123, 52, 0.15)',
  bgHover: 'rgba(30, 123, 52, 0.25)',
  textPrimary: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, 0.9)',
  textMuted: 'rgba(255, 255, 255, 0.5)',
  accent: '#1E7B34',
  accentGreen: '#1E7B34',
  accentOrange: '#C4A000',
  error: '#8B0000',
  success: '#1E7B34',
  border: 'rgba(30, 123, 52, 0.3)',
  borderAccent: 'rgba(30, 123, 52, 0.6)',
  gradient: 'linear-gradient(135deg, #1E7B34 0%, #145A25 100%)',
  scrollbarThumb: 'rgba(30, 123, 52, 0.5)',
};

// CSS Variables injection - Heisenberg Theme Only
export const GlobalAppStyles = () => {
  const t = THEME;

  return (
    <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;500;600;700&display=swap');
    @import url('https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@300;400;500;600;700&display=swap');
    
    :root {
      /* Fixed App Dimensions */
      --nav-height: ${APP_STYLES.navHeight}px;
      --header-height: ${APP_STYLES.headerHeight}px;
      --button-height: ${APP_STYLES.buttonHeight}px;
      --input-height: ${APP_STYLES.inputHeight}px;
      --input-height-sm: ${APP_STYLES.inputHeightSm}px;
      --card-radius: ${APP_STYLES.cardRadius}px;
      --max-content-width: ${APP_STYLES.maxContentWidth}px;
      
      /* Spacing - Mobile optimized */
      --spacing-xs: ${APP_STYLES.spacingXs}px;
      --spacing-sm: ${APP_STYLES.spacingSm}px;
      --spacing-md: ${APP_STYLES.spacingMd}px;
      --spacing-lg: ${APP_STYLES.spacingLg}px;
      --spacing-xl: ${APP_STYLES.spacingXl}px;
      
      /* Typography */
      --font-size-xs: ${APP_STYLES.fontSizeXs}px;
      --font-size-sm: ${APP_STYLES.fontSizeSm}px;
      --font-size-base: ${APP_STYLES.fontSizeBase}px;
      --font-size-md: ${APP_STYLES.fontSizeMd}px;
      --font-size-lg: ${APP_STYLES.fontSizeLg}px;
      --font-size-xl: ${APP_STYLES.fontSizeXl}px;
      --font-size-xxl: ${APP_STYLES.fontSizeXxl}px;
      
      /* Line Heights */
      --line-height-tight: 1.2;
      --line-height-normal: 1.5;
      --line-height-relaxed: 1.7;
      
      /* Theme Colors */
      --bg-primary: ${t.bgPrimary};
      --bg-secondary: ${t.bgSecondary};
      --bg-tertiary: ${t.bgTertiary};
      --bg-card: ${t.bgCard};
      --bg-input: ${t.bgInput};
      --bg-hover: ${t.bgHover};
      --text-primary: ${t.textPrimary};
      --text-secondary: ${t.textSecondary};
      --text-muted: ${t.textMuted};
      --accent: ${t.accent};
      --accent-green: ${t.accentGreen};
      --accent-orange: ${t.accentOrange};
      --error: ${t.error};
      --success: ${t.success};
      --border: ${t.border};
      --border-accent: ${t.borderAccent};
      --gradient: ${t.gradient};
      
      /* Safe areas for notched phones */
      --safe-area-top: env(safe-area-inset-top, 0px);
      --safe-area-bottom: env(safe-area-inset-bottom, 0px);
      --safe-area-left: env(safe-area-inset-left, 0px);
      --safe-area-right: env(safe-area-inset-right, 0px);
    }
    
    * {
      box-sizing: border-box;
      -webkit-tap-highlight-color: transparent;
      -webkit-touch-callout: none;
    }
    
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      max-width: 100vw;
      font-family: 'Roboto Condensed', -apple-system, BlinkMacSystemFont, sans-serif;
      font-size: var(--font-size-base);
      line-height: var(--line-height-normal);
      background: var(--bg-primary);
      color: var(--text-primary);
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    
    #root {
      margin: 0;
      padding: 0;
      min-height: 100%;
      width: 100%;
      max-width: 100vw;
    }
    
    /* Prevent iOS zoom on input focus */
    input, select, textarea, button {
      font-size: 16px !important;
      font-family: 'Roboto Condensed', -apple-system, BlinkMacSystemFont, sans-serif;
      touch-action: manipulation;
    }
    
    /* Better touch targets */
    button, a, input, select, textarea {
      min-height: 40px;
    }
    
    /* Prevent horizontal overflow */
    * {
      max-width: 100%;
    }
    
    img, video, iframe {
      max-width: 100%;
      height: auto;
    }
    
    /* Mobile-friendly flex containers */
    @media (max-width: 380px) {
      [style*="display: flex"]:not([style*="flex-direction: column"]) {
        flex-wrap: wrap !important;
      }
    }
    
    /* Ensure flex children don't overflow */
    [style*="flex: 1"], [style*="flex:1"] {
      min-width: 0;
    }
    
    /* Scrollbar styling */
    ::-webkit-scrollbar {
      width: 4px;
      height: 4px;
    }
    
    ::-webkit-scrollbar-track {
      background: transparent;
    }
    
    ::-webkit-scrollbar-thumb {
      background: ${t.scrollbarThumb};
      border-radius: 2px;
    }
    
    /* Heisenberg Theme Styles */
    h1, h2, h3, .heisenberg-title {
      font-family: 'Bebas Neue', 'Oswald', sans-serif !important;
      letter-spacing: 2px;
    }
    
    .periodic-tile {
      background: #1E7B34;
      border: none;
      border-radius: 4px;
      padding: 8px 12px;
      position: relative;
      box-shadow: 2px 2px 0 rgba(0, 0, 0, 0.3);
    }
    
    .periodic-tile::before {
      content: attr(data-number);
      position: absolute;
      top: 4px;
      right: 6px;
      font-size: 10px;
      color: rgba(255, 255, 255, 0.7);
    }
    
    /* Animations */
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    @keyframes slideUp {
      from { transform: translateY(16px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    
    @keyframes slideIn {
      from { transform: translateX(-16px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.02); opacity: 0.8; }
    }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    
    @keyframes gradient-shift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    
    /* Utility classes */
    .gradient-text {
      background: ${t.gradient};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    
    .glass-card {
      background: var(--bg-card);
      backdrop-filter: blur(20px);
      border: 1px solid var(--border);
      border-radius: var(--card-radius);
    }
    
    .btn-primary {
      background: #1E7B34;
      border: none;
      border-radius: 4px;
      color: #ffffff;
      font-weight: 700;
      font-size: var(--font-size-sm) !important;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 0 var(--spacing-lg);
      min-height: var(--button-height);
      box-shadow: 3px 3px 0 rgba(0, 0, 0, 0.4);
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    .btn-primary:active {
      transform: scale(0.98);
      opacity: 0.9;
    }
    
    .btn-primary:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
    
    .btn-secondary {
      background: rgba(30, 123, 52, 0.15);
      border: 1px solid var(--border-accent);
      border-radius: 4px;
      color: var(--accent);
      font-weight: 600;
      font-size: var(--font-size-sm) !important;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 0 var(--spacing-lg);
      min-height: var(--button-height);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .btn-secondary:active {
      background: rgba(99, 102, 241, 0.2);
      transform: scale(0.98);
    }
    
    .input-field {
      width: 100%;
      height: var(--input-height);
      padding: 0 14px;
      background: linear-gradient(145deg, rgba(0, 0, 0, 0.6) 0%, rgba(10, 10, 10, 0.8) 100%);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 8px;
      color: var(--text-primary);
      font-family: 'Roboto Condensed', sans-serif;
      font-size: 15px !important;
      font-weight: 500;
      letter-spacing: 0.3px;
      transition: all 0.2s ease;
      box-shadow:
        inset 2px 2px 6px rgba(0, 0, 0, 0.5),
        inset -1px -1px 3px rgba(255, 255, 255, 0.03),
        0 1px 0 rgba(255, 255, 255, 0.02);
    }
    
    .input-field-sm {
      height: var(--input-height-sm);
      padding: 0 12px;
      font-size: 13px !important;
    }
    
    .input-field:focus {
      outline: none;
    }
    
    .input-field:hover:not(:focus):not(:disabled) {
      border-color: rgba(255, 255, 255, 0.12) !important;
      background: linear-gradient(145deg, rgba(5, 5, 5, 0.7) 0%, rgba(12, 12, 12, 0.85) 100%) !important;
    }
    
    .input-field::placeholder {
      color: rgba(255, 255, 255, 0.3);
      transition: opacity 0.2s ease;
    }
    
    .input-field:focus::placeholder {
      color: rgba(255, 255, 255, 0.5);
      opacity: 1;
    }
    
    .input-field:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    /* Number inputs - ensure content is visible */
    input[type="number"] {
      min-height: 36px;
      padding: 8px 12px !important;
      font-size: 14px !important;
      font-weight: 500;
      color: #ffffff;
      background: rgba(0, 0, 0, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 6px;
      text-align: center;
    }
    
    input[type="number"]:focus {
      outline: none;
      border-color: var(--accent);
      background: rgba(0, 0, 0, 0.5);
    }
    
    input[type="number"]::-webkit-inner-spin-button,
    input[type="number"]::-webkit-outer-spin-button {
      opacity: 0.6;
    }
    
    /* Text inputs - ensure content is visible */
    input[type="text"]:not(.input-field) {
      min-height: 36px;
      padding: 8px 12px;
      font-size: 14px;
      font-weight: 500;
      color: #ffffff;
    }
    
    select.input-field {
      cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L6 6L11 1' stroke='%234CAF50' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 14px center;
      padding-right: 40px;
    }
    
    /* Card styles */
    .card {
      background: var(--bg-card);
      border: 1px solid var(--border);
      border-radius: var(--card-radius);
      padding: var(--spacing-md);
      margin-bottom: var(--spacing-md);
    }
    
    /* Section spacing */
    .section {
      margin-bottom: var(--spacing-lg);
    }
    
    .section-header {
      display: flex;
      align-items: center;
      gap: var(--spacing-sm);
      margin-bottom: var(--spacing-md);
      padding-top: var(--spacing-sm);
    }
    
    .section-title {
      font-size: var(--font-size-xs);
      font-weight: 600;
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    /* List item spacing */
    .list-item {
      padding: var(--spacing-md);
      margin-bottom: var(--spacing-sm);
      border-radius: var(--card-radius);
      background: var(--bg-card);
      border: 1px solid var(--border);
    }
    
    .list-item:last-child {
      margin-bottom: 0;
    }
    
    /* Wallet adapter overrides - RV Image Button (main trigger only) */
    .wallet-adapter-dropdown > .wallet-adapter-button-trigger {
      background: url('/images/rv.png') center/contain no-repeat !important;
      background-color: transparent !important;
      color: transparent !important;
      font-size: 0 !important;
      border: none !important;
      border-radius: 0 !important;
      height: 80px !important;
      min-width: 200px !important;
      width: 200px !important;
      padding: 0 !important;
      box-shadow: none !important;
      position: relative !important;
      overflow: hidden !important;
      cursor: pointer !important;
      transition: transform 0.15s ease, filter 0.15s ease !important;
    }
    
    /* Hide text inside main wallet button */
    .wallet-adapter-dropdown > .wallet-adapter-button-trigger * {
      visibility: hidden !important;
      font-size: 0 !important;
    }
    
    /* Hide pseudo-elements for RV button */
    .wallet-adapter-dropdown > .wallet-adapter-button-trigger::before,
    .wallet-adapter-dropdown > .wallet-adapter-button-trigger::after {
      display: none !important;
    }
    
    .wallet-adapter-dropdown > .wallet-adapter-button-trigger:hover {
      transform: scale(1.05) !important;
      filter: brightness(1.1) !important;
    }
    
    .wallet-adapter-dropdown > .wallet-adapter-button-trigger:active {
      transform: scale(0.98) !important;
    }
    
    /* Hide wallet icon in header button */
    .wallet-adapter-dropdown > .wallet-adapter-button-trigger .wallet-adapter-button-start-icon {
      display: none !important;
    }
    
    /* Wallet Modal - Breaking Bad Periodic Table Theme */
    .wallet-adapter-modal-wrapper {
      background: rgba(0, 0, 0, 0.9) !important;
      backdrop-filter: blur(8px) !important;
      z-index: 99999 !important;
      position: fixed !important;
      inset: 0 !important;
    }
    
    .wallet-adapter-modal {
      background: #F4D03F !important;
      border: none !important;
      border-radius: 0 !important;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8) !important;
      font-family: 'Roboto Condensed', sans-serif !important;
      max-width: 380px !important;
      position: relative !important;
      overflow: visible !important;
    }
    
    /* Hazmat stripe top */
    .wallet-adapter-modal::before {
      content: '' !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      height: 32px !important;
      background: repeating-linear-gradient(-45deg, #F4D03F 0px, #F4D03F 15px, #000 15px, #000 30px) !important;
      border-bottom: 2px solid #000 !important;
      z-index: 1 !important;
    }
    
    /* Hazmat stripe bottom */
    .wallet-adapter-modal::after {
      content: '' !important;
      position: absolute !important;
      bottom: 0 !important;
      left: 0 !important;
      right: 0 !important;
      height: 32px !important;
      background: repeating-linear-gradient(-45deg, #F4D03F 0px, #F4D03F 15px, #000 15px, #000 30px) !important;
      border-top: 2px solid #000 !important;
      z-index: 1 !important;
    }
    
    .wallet-adapter-modal-container {
      padding: 48px 20px 48px 20px !important;
    }
    
    .wallet-adapter-modal-title {
      font-family: 'Bebas Neue', sans-serif !important;
      color: #000 !important;
      font-size: 28px !important;
      letter-spacing: 4px !important;
      text-transform: uppercase !important;
      text-align: center !important;
      margin-bottom: 8px !important;
    }
    
    /* Subtitle text */
    .wallet-adapter-modal-middle {
      color: rgba(0, 0, 0, 0.6) !important;
      font-family: 'Bebas Neue', sans-serif !important;
      font-size: 12px !important;
      letter-spacing: 2px !important;
      text-transform: uppercase !important;
      text-align: center !important;
    }
    
    .wallet-adapter-modal-button-close {
      background: #000 !important;
      border: none !important;
      border-radius: 0 !important;
      width: 32px !important;
      height: 32px !important;
      top: 40px !important;
      right: 12px !important;
    }
    
    .wallet-adapter-modal-button-close:hover {
      background: #1E7B34 !important;
    }
    
    .wallet-adapter-modal-button-close svg {
      fill: #F4D03F !important;
      width: 14px !important;
      height: 14px !important;
    }
    
    /* Wallet list items in modal - periodic table tiles */
    .wallet-adapter-modal-list {
      margin: 16px 0 0 0 !important;
      padding: 0 !important;
    }
    
    .wallet-adapter-modal-list li {
      margin-bottom: 8px !important;
    }
    
    .wallet-adapter-modal-list .wallet-adapter-button {
      width: 100% !important;
      background: linear-gradient(135deg, #1E7B34 0%, #145A25 100%) !important;
      border: 2px solid rgba(30, 123, 52, 0.7) !important;
      border-radius: 4px !important;
      padding: 14px 16px !important;
      height: auto !important;
      justify-content: flex-start !important;
      position: relative !important;
      box-shadow: 3px 3px 0 rgba(0, 0, 0, 0.5) !important;
    }
    
    .wallet-adapter-modal-list .wallet-adapter-button::before {
      display: none !important;
    }
    
    .wallet-adapter-modal-list .wallet-adapter-button::after {
      display: none !important;
    }
    
    .wallet-adapter-modal-list .wallet-adapter-button:hover {
      background: linear-gradient(135deg, #2E8B3E 0%, #1E7B34 100%) !important;
      transform: translateY(-1px) !important;
      box-shadow: 4px 4px 0 rgba(0, 0, 0, 0.5) !important;
    }
    
    .wallet-adapter-modal-list .wallet-adapter-button:active {
      transform: translateY(1px) !important;
      box-shadow: 2px 2px 0 rgba(0, 0, 0, 0.5) !important;
    }
    
    .wallet-adapter-modal-list .wallet-adapter-button span {
      font-family: 'Bebas Neue', sans-serif !important;
      font-size: 18px !important;
      letter-spacing: 2px !important;
      color: #ffffff !important;
      text-transform: uppercase !important;
    }
    
    /* Show wallet icons in modal */
    .wallet-adapter-modal-list .wallet-adapter-button img {
      display: block !important;
      width: 28px !important;
      height: 28px !important;
      margin-right: 12px !important;
      border-radius: 4px !important;
    }
    
    .wallet-adapter-modal-list .wallet-adapter-button .wallet-adapter-button-start-icon {
      display: flex !important;
      width: 28px !important;
      height: 28px !important;
      margin-right: 12px !important;
    }
    
    /* "More options" button */
    .wallet-adapter-modal-list-more {
      background: transparent !important;
      color: #000 !important;
      border: 2px dashed rgba(0, 0, 0, 0.3) !important;
      border-radius: 4px !important;
      font-family: 'Bebas Neue', sans-serif !important;
      font-size: 14px !important;
      letter-spacing: 2px !important;
      text-transform: uppercase !important;
      cursor: pointer !important;
      padding: 12px !important;
    }
    
    .wallet-adapter-modal-list-more:hover {
      background: rgba(0, 0, 0, 0.1) !important;
      border-color: #000 !important;
    }
    
    /* Detected/Installed badge */
    .wallet-adapter-modal-list .wallet-adapter-button-end-icon {
      background: #F4D03F !important;
      color: #000 !important;
      font-size: 10px !important;
      padding: 3px 8px !important;
      border-radius: 2px !important;
      font-family: 'Bebas Neue', sans-serif !important;
      letter-spacing: 1px !important;
      font-weight: bold !important;
    }
    
    /* Collapse button styling */
    .wallet-adapter-collapse {
      background: transparent !important;
    }
    
    .wallet-adapter-collapse-button {
      background: transparent !important;
      color: #000 !important;
      font-family: 'Bebas Neue', sans-serif !important;
      letter-spacing: 2px !important;
    }
    
    .wallet-adapter-collapse-button svg {
      fill: #000 !important;
    }
  `}</style>
  );
};

export default GlobalAppStyles;
